import {
  IconButton
} from "./chunk-R7XHNGZL.js";
import {
  FontAwesomeIcon
} from "./chunk-APS5QAFW.js";
import {
  Button
} from "./chunk-JSCFMZUG.js";
import {
  cn,
  twVariants
} from "./chunk-LJ5F4WFH.js";

// src/Toast/Toast.tsx
import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-fit",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants = twVariants({
  slots: {
    root: [
      "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md py-4 pl-5 pr-3 transition-all",
      "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
      "border border-l-8 bg-surface-white font-body text-neutral-primary shadow-4",
      "max-w-[600px]",
      "border-none before:absolute before:left-0 before:top-0 before:h-full before:w-2"
    ],
    title: "text-body-2 font-semibold",
    description: "text-inherit text-body-2"
  },
  variants: {
    variant: {
      info: {
        root: "before:bg-[var(--kiiwi-border-alert-information)]"
        // this pseudo-element is semantically a border. We don't need to duplicate those tokens.
      },
      success: {
        root: "before:bg-[var(--kiiwi-border-alert-success)]"
      },
      warning: {
        root: "before:bg-[var(--kiiwi-border-alert-warning)]"
      },
      error: {
        root: "before:bg-[var(--kiiwi-border-alert-danger)]"
      }
    },
    closable: {
      true: {}
    }
  },
  defaultVariants: {
    variant: "info"
  }
});
var Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  const { root } = toastVariants({ variant });
  return /* @__PURE__ */ React.createElement(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(root({ variant }), className),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ React.createElement(ToastPrimitives.Action, { ref, asChild: true, ...props }, /* @__PURE__ */ React.createElement(Button, { variant: "ghost", size: "sm", className: cn("-my-3", props.className) }, children)));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React.forwardRef((props, ref) => /* @__PURE__ */ React.createElement(ToastPrimitives.Close, { ref, asChild: true, "toast-close": "", ...props }, /* @__PURE__ */ React.createElement(
  IconButton,
  {
    variant: "ghost",
    size: "sm",
    className: cn("-my-3", props.className)
  },
  /* @__PURE__ */ React.createElement(
    FontAwesomeIcon,
    {
      name: "xmark:regular",
      className: "h-4 w-4 [&_svg]:text-neutral-primary"
    }
  )
)));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React.forwardRef(({ className, ...props }, ref) => {
  const { title } = toastVariants();
  return /* @__PURE__ */ React.createElement(
    ToastPrimitives.Title,
    {
      ref,
      className: cn(title(), className),
      ...props
    }
  );
});
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { description } = toastVariants();
  return /* @__PURE__ */ React.createElement(
    ToastPrimitives.Description,
    {
      ref,
      className: cn(description(), className),
      ...props
    }
  );
});
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// src/Toast/UseToast.tsx
import * as React2 from "react";
var TOAST_LIMIT = 1;
var TOAST_REMOVE_DELAY = 1e6;
var count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
var toastTimeouts = /* @__PURE__ */ new Map();
var addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
var reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
var listeners = [];
var memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
}
function useToast() {
  const [state, setState] = React2.useState(memoryState);
  React2.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}

// src/Toast/Toaster.tsx
import React3 from "react";
var variantToIconMapping = {
  info: "circle-info:regular",
  error: "circle-xmark:regular",
  success: "circle-check:regular",
  warning: "triangle-exclamation:regular"
};
function Toaster({
  viewportClassName,
  children
}) {
  const { toasts } = useToast();
  return /* @__PURE__ */ React3.createElement(ToastProvider, null, children, toasts.map(function({
    id,
    title,
    description,
    action,
    closable,
    variant = "info",
    ...props
  }) {
    const iconName = variantToIconMapping[variant];
    return /* @__PURE__ */ React3.createElement(Toast, { key: id, ...props, variant }, /* @__PURE__ */ React3.createElement("div", { className: "grid grid-flow-col items-center gap-3" }, /* @__PURE__ */ React3.createElement(
      FontAwesomeIcon,
      {
        name: iconName,
        className: cn(
          "inline-block h-4 w-4",
          variant === "info" && "text-information-alert-icon",
          variant === "success" && "text-success-alert-icon",
          variant === "warning" && "text-warning-alert-icon",
          variant === "error" && "text-danger-alert-icon"
        )
      }
    ), /* @__PURE__ */ React3.createElement("div", { className: "grid gap-1 text-left" }, title && /* @__PURE__ */ React3.createElement(ToastTitle, null, title), description && /* @__PURE__ */ React3.createElement(ToastDescription, null, description))), action, closable && /* @__PURE__ */ React3.createElement(ToastClose, null));
  }), /* @__PURE__ */ React3.createElement(ToastViewport, { className: viewportClassName }));
}

export {
  ToastProvider,
  ToastViewport,
  toastVariants,
  Toast,
  ToastAction,
  ToastClose,
  ToastTitle,
  ToastDescription,
  reducer,
  toast,
  useToast,
  variantToIconMapping,
  Toaster
};
//# sourceMappingURL=chunk-46WA4YUA.js.map